import React, { useState, useEffect } from "react";
import {
  Currency,
  ItineraryItem,
  Participant,
  TripLodging,
  ActivityParticipant,
  type ActivityItem as ActivityItemType,
} from "@/types";
import { Card, CardContent } from "@/components/ui/card";
import { TimePicker } from "antd";
import { Button } from "@/components/ui/button";
import dayjs, { Dayjs } from "dayjs";
import Divider from "@tripbuilder/cat-experiences/divider";
import {
  formatImageUrl,
  formatPrice,
  formatReviewCount,
  formatReviewScore,
} from "@/lib/general";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import CustomDropdown from "@/components/ui/CustomDropdown";
import waytoraLogo from "@/assets/logos/internal/waytora_white_sm_logo.svg";
import { Tooltip } from "@heroui/tooltip";
import { useBookingStatus } from "@/hooks/useBookingStatus";
import { PlusIcon, XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BookingModal } from "@/components/common/BookingModal";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import LazyLoad from "../image/LazyLoad";
import logger from "@/utils/logger";

interface ActivityParticipantWithStatus extends Participant {
  status: "confirmed" | "declined" | "pending";
}

interface ActivityProps {
  activity: ItineraryItem;
  tripId: string;
  nextActivity: ActivityItemType | null;
  activities: ActivityItemType[];
  locationName: string;
  locationTimeZone: string;
  locationParticipants: Participant[];
  onUpdateActivityParticipants: (
    activityId: string,
    participants: ActivityParticipant[],
    newStatus: "confirmed" | "pending" | "declined",
    onError?: () => void
  ) => Promise<void>;
  onDelete: (id: string, name: string) => void;
  handleTimeChange: (time: Date, activityId: string) => void;
  handleOpenModal: (item: ItineraryItem | TripLodging) => void;
  selectedCurrency: Currency;
  selectedDay: string | null;
  selectedLocationId: string | null;
  isAdmin: boolean;
}

const Activity: React.FC<ActivityProps> = ({
  activity,
  tripId,
  nextActivity,
  activities,
  locationName,
  locationTimeZone,
  locationParticipants,
  onUpdateActivityParticipants,
  onDelete,
  handleTimeChange,
  handleOpenModal,
  selectedCurrency,
  selectedDay,
  selectedLocationId,
  isAdmin,
}) => {
  const { updateBookingStatus, isUpdating } = useBookingStatus();
  const [isOpen, setIsOpen] = useState(false);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [lastBookingAttemptTime, setLastBookingAttemptTime] = useState<
    number | null
  >(null);

  const [activityParticipants, setActivityParticipants] = useState<
    ActivityParticipantWithStatus[]
  >(() => {
    // Initialize with activity's existing participants and their status
    return (
      activity.participants?.map((p) => ({
        id: p.id,
        name: p.name,
        profile_image: p.profile_image,
        status: (p as any).status || "confirmed", // Handle existing status or default to confirmed
      })) || []
    );
  });

  const [updatingParticipants, setUpdatingParticipants] = useState<Set<string>>(
    new Set()
  );

  // Update local state when activity.participants changes from parent
  useEffect(() => {
    if (activity.participants) {
      setActivityParticipants(
        activity.participants.map((p) => ({
          id: p.id,
          name: p.name,
          profile_image: p.profile_image,
          status:
            (p as any).status ||
            activityParticipants.find((ap) => ap.id === p.id)?.status ||
            "confirmed",
        }))
      );
    }
  }, [activity.participants]);

  // Check if user returned from booking page
  useEffect(() => {
    const checkIfReturnedFromBooking = () => {
      if (lastBookingAttemptTime) {
        const timeElapsed = Date.now() - lastBookingAttemptTime;
        // If less than 15 minutes have passed since booking attempt
        if (timeElapsed < 15 * 60 * 1000) {
          setShowBookingConfirmation(true);
          setLastBookingAttemptTime(null);
        }
      }
    };

    // Check when window regains focus
    const handleFocus = () => {
      checkIfReturnedFromBooking();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastBookingAttemptTime]);

  const getUpdatedParticipants = (
    currentParticipants: ActivityParticipantWithStatus[],
    user: Participant,
    newStatus: ActivityParticipantWithStatus["status"]
  ): ActivityParticipantWithStatus[] => {
    const existingIndex = currentParticipants.findIndex(
      (p) => p.id === user.id
    );

    if (existingIndex !== -1) {
      const existing = currentParticipants[existingIndex];
      if (!existing) {
        return currentParticipants;
      }

      if (existing.status === newStatus) {
        // Remove if clicking same status
        return currentParticipants.filter((p) => p.id !== user.id);
      } else {
        // Update status
        const updated = [...currentParticipants];
        updated[existingIndex] = {
          ...user,
          status: newStatus,
        };
        return updated;
      }
    } else {
      // Add new participant
      return [
        ...currentParticipants,
        {
          ...user,
          status: newStatus,
        },
      ];
    }
  };

  const handleActivityParticipantToggle = async (
    user: Participant,
    newStatus: ActivityParticipantWithStatus["status"]
  ) => {
    if (updatingParticipants.has(user.id)) {
      return;
    }

    logger.log("Updating participant status", user, newStatus);

    setUpdatingParticipants((prev) => new Set(prev).add(user.id));

    try {
      // Store previous state for rollback
      const previousParticipants = [...activityParticipants];

      // Optimistically update local state
      const updatedParticipants = getUpdatedParticipants(
        activityParticipants,
        user,
        newStatus
      );
      setActivityParticipants(updatedParticipants);

      // Update server with participants including status
      const backendParticipants = updatedParticipants.map(
        ({ id, name, profile_image, status }) => ({
          id,
          name,
          profile_image,
          status,
        })
      );

      // Update server
      await onUpdateActivityParticipants(
        activity.id,
        backendParticipants,
        newStatus,
        () => {
          // Rollback on error
          setActivityParticipants(previousParticipants);
          toast({
            title: "Error updating participants",
            description: "Please try again",
            variant: "destructive",
          });
        }
      );

      toast({
        title: "Updated participants",
        description: "Changes saved successfully",
      });
    } catch (error) {
      console.error("Error updating participant status:", error);
    } finally {
      setUpdatingParticipants((prev) => {
        const updated = new Set(prev);
        updated.delete(user.id);
        return updated;
      });
    }
  };

  const onChange = (time: Dayjs | null) => {
    if (time) {
      handleTimeChange(time.toDate(), activity.id);
    }
  };

  const handleViatorClick = () => {
    if (!selectedDay || !selectedLocationId) return;
    if (activity.bookingStatus !== "booked") {
      setShowBookingModal(true);
    } else {
      updateBookingStatus(
        "activity",
        activity.id,
        "not_booked",
        selectedLocationId,
        selectedDay
      );
    }
  };

  const handleProviderBooked = () => {
    setLastBookingAttemptTime(Date.now());
    setShowBookingModal(false);
  };

  const handleConfirmBooking = () => {
    if (!selectedDay || !selectedLocationId) return;
    updateBookingStatus(
      "activity",
      activity.id,
      "booked",
      selectedLocationId,
      selectedDay
    );
    setShowBookingConfirmation(false);
  };

  const time = activity.start_time
    ? dayjs(activity.start_time).tz(locationTimeZone)
    : undefined;

  return (
    <div className="activity-item w-full">
      <Card className="my-3 rounded-2xl border-neutral-200/75 hover:shadow transition-all duration-300 ease-in-out">
        <CardContent className="p-3 sm:p-3">
          <div className="flex flex-col sm:flex-row items-start gap-4 h-[9rem]">
            {/* Image Section */}
            <div className="w-full sm:w-[10rem] h-[9rem] rounded-lg sm:rounded-l-xl sm:rounded-r-md overflow-hidden flex-shrink-0 relative">
              {activity.images && activity.images[0] ? (
                <>
                  <LazyLoad
                    className="h-full w-full object-cover"
                    src={formatImageUrl(activity.images[0], "medium")}
                    lowResSrc={formatImageUrl(activity.images[0], "low")}
                    alt="Experience Image"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={activity.type === "community"}
                    className={`absolute top-2 left-2 text-white hover:text-white bg-transparent hover:bg-white/15 backdrop-blur h-5 w-5 2xl:h-6 2xl:w-6 duration-300 ease-in-out rounded-full ${
                      activity.type === "community" &&
                      "opacity-50 cursor-default"
                    }`}
                    onClick={() => handleOpenModal(activity)}
                  >
                    <i className="fal fa-circle-info text-xs 2xl:text-sm" />
                  </Button>
                </>
              ) : (
                <div className="h-full w-full bg-neutral-200 flex items-center justify-center">
                  <i className="fas fa-image text-neutral-400 text-3xl" />
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={activity.type === "community"}
                    className={`absolute top-2 left-2 text-neutral-600 hover:bg-slate-100 hover:text-slate-700 h-6 w-6 duration-300 ease-in-out rounded-full ${
                      activity.type === "community" &&
                      "opacity-50 cursor-default"
                    }`}
                    onClick={() => handleOpenModal(activity)}
                  >
                    <i className="fal fa-circle-info text-[10px]" />
                  </Button>
                </div>
              )}
            </div>

            {/* Content Section */}
            <div className="flex-1 min-w-0 flex flex-col h-full justify-between w-full">
              <div className="w-full">
                <div className="flex items-center flex-wrap gap-2">
                  <h2 className="text-base font-medium text-neutral-800 truncate max-w-[calc(100%-8rem)]">
                    {activity.name || ""}
                  </h2>
                  <TimePicker
                    onChange={onChange}
                    value={time}
                    variant="borderless"
                    suffixIcon={null}
                    minuteStep={5}
                    hourStep={1}
                    name="start_time"
                    size="small"
                    format="h:mm a"
                    use12Hours={true}
                    needConfirm={false}
                    className="text-neutral-800 font-light rounded-md focus:ring-indigo-500 focus:outline-none duration-200 transition ease-in-out placeholder-neutral-400 hover:cursor-pointer"
                  />
                </div>

                {/* Info boxes */}
                <div className="flex gap-2 mt-1 mb-1.5">
                  {/* Cost */}
                  {activity.pricing > 0 ? (
                    <div className="flex items-center gap-2 bg-slate-100/90 px-2 py-0.5 rounded-md">
                      <span className="text-[10px] 2xl:text-xs text-neutral-700 font-light">
                        From{" "}
                        {formatPrice(selectedCurrency.code, activity.pricing)}
                      </span>
                    </div>
                  ) : null}

                  {/* Reviews */}
                  {activity.metadata.rating && activity.metadata.num_reviews ? (
                    <div className="flex items-center gap-1.5 bg-slate-100/90 px-2 py-0.5 rounded-md">
                      <i className="far fa-star text-neutral-700 text-[10px] 2xl:text-xs" />
                      <span className="text-[10px] 2xl:text-xs text-neutral-700 font-light">
                        {formatReviewScore(activity.metadata.rating)} (
                        {formatReviewCount(activity.metadata.num_reviews)})
                      </span>
                    </div>
                  ) : null}

                  {/* Duration */}
                  <div className="flex items-center gap-1.5 bg-slate-100/90 px-2 py-0.5 rounded-md">
                    <i className="far fa-clock text-neutral-700 text-[10px] 2xl:text-xs" />
                    <span className="text-[10px] 2xl:text-xs text-neutral-700 font-light">
                      {activity.duration} hrs
                    </span>
                  </div>
                </div>

                {activity.metadata.description && (
                  <p className="mt-3 text-xs font-light text-neutral-700 line-clamp-2">
                    {reduceExperienceDescription(activity.metadata.description)}
                  </p>
                )}
              </div>

              {/* Actions Section */}
              <div className="flex justify-between items-center w-full">
                {/* Left group of utility buttons */}
                <div className="flex space-x-2 items-center">
                  {/* People Button */}
                  {activity.type === "viator" && (
                    <CustomDropdown
                      title="Participants"
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                      className="w-full p-3"
                      position="left"
                      size="sm"
                      placement="auto"
                      trigger={
                        <div className="flex items-center">
                          {/* Show confirmed participants' avatars */}
                          <div className="flex items-center">
                            {activityParticipants
                              .filter((p) => p.status === "confirmed")
                              .slice(0, 3)
                              .map((participant, index) => (
                                <div
                                  key={participant.id}
                                  className={`w-6 h-6 2xl:w-7 2xl:h-7 rounded-full overflow-hidden ring-2 ring-white ${
                                    index !== 0 ? "-ml-2" : ""
                                  }`}
                                  style={{
                                    zIndex: activityParticipants.length - index,
                                  }}
                                >
                                  <AvatarImage
                                    src={participant.profile_image}
                                    alt={`${participant.name}'s avatar`}
                                    className="w-full h-full"
                                    firstName={participant.name.split(" ")[0]}
                                    lastName={participant.name.split(" ")[1]}
                                  />
                                </div>
                              ))}
                            {activityParticipants.filter(
                              (p) => p.status === "confirmed"
                            ).length > 3 && (
                              <div
                                className="w-6 h-6 2xl:w-7 2xl:h-7 rounded-full ring-2 ring-white -ml-2 bg-slate-100 flex items-center justify-center"
                                style={{
                                  zIndex: 1,
                                }}
                              >
                                <span className="text-[10px] 2xl:text-xs text-slate-600 font-medium">
                                  +
                                  {activityParticipants.filter(
                                    (p) => p.status === "confirmed"
                                  ).length - 3}
                                </span>
                              </div>
                            )}
                            <div
                              className={`w-6 h-6 2xl:w-7 2xl:h-7 rounded-full ring-1 ring-neutral-200 ${
                                activityParticipants.length > 0
                                  ? "-ml-2"
                                  : "ml-0"
                              } bg-white hover:bg-slate-100 flex items-center justify-center cursor-pointer transition-colors duration-300`}
                              style={{
                                zIndex: 0,
                              }}
                            >
                              <PlusIcon className="w-3 h-3 2xl:w-4 2xl:h-4 text-neutral-600" />
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <div className="flex flex-col space-y-2 max-h-[10rem] overflow-y-auto no-scrollbar">
                        {locationParticipants.length === 0 ? (
                          <div className="flex items-center justify-center p-4">
                            <p className="text-xs text-neutral-500 text-center">
                              Add members to {locationName} to assign activities
                            </p>
                          </div>
                        ) : (
                          locationParticipants.map((user) => {
                            const participant = activityParticipants.find(
                              (p) => p.id === user.id
                            );
                            const isUpdating = updatingParticipants.has(
                              user.id
                            );

                            return (
                              <div
                                key={user.id}
                                className="flex items-center gap-4 p-1 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
                                onSelect={(e) => e.preventDefault()}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="relative w-7 h-7 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white">
                                    <AvatarImage
                                      src={user.profile_image}
                                      alt={`${user.name}'s avatar`}
                                      className="w-full h-full object-cover"
                                      firstName={user.name.split(" ")[0]}
                                      lastName={user.name.split(" ")[1]}
                                    />
                                  </div>
                                  <span className="text-xs text-neutral-700 font-light truncate">
                                    {user.name}
                                  </span>
                                </div>

                                <div className="flex gap-2 flex-shrink-0">
                                  {/* Confirm button */}
                                  <button
                                    disabled={isUpdating}
                                    className={cn(
                                      "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200",
                                      isUpdating &&
                                        "opacity-50 cursor-not-allowed",
                                      participant?.status === "confirmed"
                                        ? "bg-green-500 text-white hover:bg-green-600"
                                        : "bg-white border border-neutral-200 text-neutral-400 hover:border-green-500 hover:text-green-500"
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isUpdating) {
                                        handleActivityParticipantToggle(
                                          user,
                                          "confirmed"
                                        );
                                      }
                                    }}
                                  >
                                    <i className="fa-solid fa-check text-[10px]" />
                                  </button>

                                  {/* Maybe button */}
                                  <button
                                    disabled={isUpdating}
                                    className={cn(
                                      "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200",
                                      isUpdating &&
                                        "opacity-50 cursor-not-allowed",
                                      participant?.status === "pending"
                                        ? "bg-amber-500 text-white hover:bg-amber-600"
                                        : "bg-white border border-neutral-200 text-neutral-400 hover:border-amber-500 hover:text-amber-500"
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isUpdating) {
                                        handleActivityParticipantToggle(
                                          user,
                                          "pending"
                                        );
                                      }
                                    }}
                                  >
                                    <i className="fa-solid fa-question text-[10px]" />
                                  </button>

                                  {/* Decline button */}
                                  <button
                                    disabled={isUpdating}
                                    className={cn(
                                      "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200",
                                      isUpdating &&
                                        "opacity-50 cursor-not-allowed",
                                      participant?.status === "declined"
                                        ? "bg-rose-500 text-white hover:bg-rose-600"
                                        : "bg-white border border-neutral-200 text-neutral-400 hover:border-rose-500 hover:text-rose-500"
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isUpdating) {
                                        handleActivityParticipantToggle(
                                          user,
                                          "declined"
                                        );
                                      }
                                    }}
                                  >
                                    <i className="fa-solid fa-xmark text-[10px]" />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </CustomDropdown>
                  )}

                  {/* Delete Button */}
                  {isAdmin && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-neutral-600 h-8 w-8 2xl:h-9 2xl:w-9 hover:bg-rose-50 hover:text-rose-500 duration-300 ease-in-out rounded-full"
                      onClick={() => onDelete(activity.id, activity.name || "")}
                    >
                      <i className="fal fa-trash text-sm 2xl:text-base" />
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Book Now Button */}
                  {activity.type === "viator" && (
                    <button
                      onClick={handleViatorClick}
                      className={`${
                        activity.bookingStatus === "booked"
                          ? "bg-green-50 text-green-600 border border-green-500 hover:bg-neutral-100 hover:text-neutral-600 hover:border-neutral-500 group"
                          : "bg-indigo-50 hover:bg-indigo-100 text-indigo-500 border border-indigo-500"
                      } w-24 h-8 2xl:w-28 2xl:h-9 rounded-full text-xs 2xl:text-sm font-light flex items-center justify-center gap-2 transition-all duration-300 ease-in-out`}
                    >
                      {activity.bookingStatus === "booked" ? (
                        <div className="flex items-center justify-center w-full">
                          <span className="flex items-center gap-1.5 opacity-100 group-hover:opacity-0">
                            Booked
                          </span>
                          <XMarkIcon className="w-4 h-4 absolute opacity-0 group-hover:opacity-100" />
                        </div>
                      ) : (
                        <span className="transition-opacity duration-300 ease-in-out">
                          Book Now
                        </span>
                      )}
                    </button>
                  )}

                  {activity.type === "community" && (
                    <Tooltip
                      content={
                        <p className="text-xs font-light">
                          Created by Waytora Community
                        </p>
                      }
                      showArrow={true}
                      closeDelay={0}
                      placement="bottom"
                    >
                      <div className="flex items-center justify-center bg-indigo-600 w-6 h-6 rounded-full">
                        <img
                          src={waytoraLogo}
                          alt="Waytora Logo"
                          className="w-4 h-4"
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        type="activities"
        tripId={tripId}
        activityData={{
          id: activity.id,
          name: activity.name || "",
          customType: "experience",
        }}
        onBooked={handleProviderBooked}
      />

      <Dialog
        open={showBookingConfirmation}
        onOpenChange={setShowBookingConfirmation}
      >
        <DialogContent className="p-6 bg-white rounded-xl">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl font-semibold">
              Did you book your activity?
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-600 font-light">
              Update your booking status for your activity
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 mt-2 rounded-xl bg-neutral-50 border border-neutral-200">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-200 flex-shrink-0">
                {activity.images?.[0] ? (
                  <img
                    src={formatImageUrl(activity.images[0], "medium")}
                    alt={activity.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="fas fa-image text-neutral-400 text-xl" />
                  </div>
                )}
              </div>
              <div className="w-full min-w-0">
                <h3 className="text-sm font-medium text-neutral-900 line-clamp-1">
                  {activity.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-xs text-neutral-500 font-light">
                    {activity.duration} hrs
                  </div>
                  <span className="w-1 h-1 bg-neutral-300 rounded-full"></span>
                  <div className="text-xs text-neutral-500 font-light">
                    {formatPrice(selectedCurrency.code, activity.pricing)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowBookingConfirmation(false)}
              className="flex-1 rounded-full font-light text-sm hover:bg-neutral-100"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Not Yet
            </Button>
            <Button
              onClick={handleConfirmBooking}
              className="flex-1 bg-green-500/10 border border-green-500/50 text-green-700 hover:bg-green-500/20 rounded-full font-light text-sm"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Yes, I booked it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {nextActivity && (
        <Divider
          prevItem={activity as ActivityItemType}
          nextItem={nextActivity}
          locationName={locationName}
          dayItems={activities}
        />
      )}
    </div>
  );
};

const reduceExperienceDescription = (description: string) => {
  const minLength = 60;
  const sentences = description
    .split(/(?![A-Z][a-z]\.)(?<=\.|\?|!)\s/g)
    .filter((s) => s.trim().length > 0);
  let result = sentences[0] || "";
  if (sentences.length > 1 && result.length < minLength) {
    result += " " + sentences[1];
  }
  result = result.trim();
  return result.length > 0 ? result : description;
};

export default Activity;
